import { Text } from 'designSystem/components/Text'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useRolodexEntriesCachedById } from 'features/knowledgeDocumentViewer/hooks/useRolodexEntriesCachedById'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { toTitleCase } from 'features/knowledgeDocumentViewer/utils/textHelpers'
import { useMemo } from 'react'
import { RolodexEntryType } from 'types'

export type DocumentInfo = {
  title: string
  filename: string
  system: string
  vendor: string
  docType: string
  drawing: string
  revisionCode: string
  revisionDate: string
}

type DocumentHeaderTitleProps = {
  documentId: string
  customTitle?: string
}

function buildSubtitle(documentInfo: DocumentInfo): string {
  const parts: string[] = []
  if (documentInfo.revisionCode && documentInfo.revisionDate) {
    parts.push(
      `Revision ${documentInfo.revisionCode} (${documentInfo.revisionDate})`
    )
  } else if (documentInfo.revisionCode) {
    parts.push(`Revision ${documentInfo.revisionCode}`)
  } else if (documentInfo.revisionDate) {
    parts.push(documentInfo.revisionDate)
  }
  if (documentInfo.system) parts.push(documentInfo.system)
  if (documentInfo.vendor) parts.push(documentInfo.vendor)
  if (documentInfo.docType) parts.push(documentInfo.docType)
  if (documentInfo.drawing) parts.push(documentInfo.drawing)
  return parts.join(' \u2022  ')
}

export function DocumentHeaderTitle({
  documentId,
  customTitle,
}: DocumentHeaderTitleProps) {
  const { siteID } = useKnowledgeDocumentDataContext()
  const {
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    pipelineConfigCacheScope,
  } = usePipelineConfig()
  const documentIds = useMemo(
    () => (isPipelineConfigScopeLoading || !documentId ? [] : [documentId]),
    [documentId, isPipelineConfigScopeLoading]
  )
  const documentLoadOptions = useMemo(
    () => ({
      type: [RolodexEntryType.SYSTEM_DOCUMENT],
      tags: getPipelineConfigScopedTags({ site: siteID }),
    }),
    [getPipelineConfigScopedTags, siteID]
  )
  const { data: systemDocumentEntries } =
    useRolodexEntriesCachedById<SystemDocumentEntry>(documentIds, undefined, {
      cacheScope: pipelineConfigCacheScope,
      loadOptions: documentLoadOptions,
    })
  const entry = systemDocumentEntries?.[0]?.entry

  const documentInfo: DocumentInfo = {
    title: entry?.data.document_title || entry?.data.title || '',
    filename: entry?.data.filename ?? '',
    system: entry?.data.system ?? '',
    vendor: entry?.data.vendor_name ?? '',
    docType: entry?.data.document_type ?? '',
    drawing: entry?.data.drawing_number ?? '',
    revisionCode: entry?.data.revision_code ?? '',
    revisionDate: entry?.data.revision_date ?? '',
  }

  const showCustomTitle = customTitle && !documentId

  const displayTitle = showCustomTitle
    ? customTitle
    : documentInfo.title
      ? toTitleCase(documentInfo.title)
      : ''

  return (
    <div>
      <Text as='p' variant='subheading'>
        {displayTitle}
      </Text>
      {!showCustomTitle && (
        <Text as='p' variant='body' color='muted'>
          {buildSubtitle(documentInfo)}
        </Text>
      )}
    </div>
  )
}
